import {
	type CSSProperties,
	type Dispatch,
	type SetStateAction,
	useEffect,
	useId,
	useMemo,
	useState
} from 'react'
import { Param, URLSearchParser } from 'shared/search-adapters'
import { campaignHooks } from 'features/campaigns'
import type { Step } from 'features/campaigns/types'
import { executionsUtils } from 'features/executions'
import { ListSelect } from 'shared/ui'

import { STATE_OPTIONS, type StateOption } from './const'
import css from './Filters.module.css'

const Option = ({ color, label }: StateOption) => (
	<span
		className={css.state}
		style={
			{
				'--active': color
			} as CSSProperties
		}
	>
		{label}
	</span>
)

function buildStateOptions(steps?: Step[] | null): StateOption[] {
	return STATE_OPTIONS.map(option => ({
		...option,
		label: executionsUtils.getExecutionTitle(
			option.value as Parameters<typeof executionsUtils.getExecutionTitle>[0],
			steps
		)
	}))
}

function getInitial(param: URLSearchParams, options: StateOption[]) {
	const globals = URLSearchParser.Array(param, Param.ExecutionStatus)

	const set = new Set(globals)

	return options.filter(option => set.has(option.value))
}

StatusSelector.useState = (param: URLSearchParams) => {
	const initialOptions = buildStateOptions(undefined)
	return useState(getInitial(param, initialOptions))
}

export function StatusSelector({
	currents,

	setter
}: {
	currents: StateOption[]
	setter: Dispatch<SetStateAction<StateOption[]>>
}) {
	const id = useId()
	const { campaign } = campaignHooks.useCurrent()

	const options = useMemo(
		() => buildStateOptions(campaign?.steps),
		[campaign?.steps]
	)

	useEffect(() => {
		if (!currents.length) {
			return;
		}

		const optionByValue = new Map(
			options.map(o => [o.value, o])
		);

		const synced = currents.map(current => {
			const fresh = optionByValue.get(current.value);
			return fresh ?? current;
		});

		const hasChanges = synced.some((opt, index) =>
			opt.label !== currents[index]?.label
		);

		if (hasChanges) {
			setter(synced);
		}
	}, [options, currents, setter]);

	return (
		<div className={css.set}>
			<label className={css.label} htmlFor={id}>
				Status
			</label>
			<ListSelect
				formatOptionLabel={Option}
				inputId={id}
				isMulti
				onChange={values => setter(Array.from(values))}
				options={options}
				placeholder='Select status'
				setter={setter}
				value={currents}
			/>
		</div>
	)
}
